import {cloneDeep} from 'lodash';
import {Loader} from '../../models/loader/loader.redux';
import {AppointmentActions, AppointmentState, IUsersAppointment} from '../../models/appointment';
import {Store} from '@ngxs/store';
import {Logger} from '../../class/helper/logger.class';
import {MealPlanAppointmentResponseDTO} from '../../dtos/meal-plan';
import {NzModalRef, NzModalService} from 'ng-zorro-antd/modal';
import {Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {INoteTimestamp, ServiceAction} from '../../models/service';
import {IUser} from '../../models/auth';
import {es} from 'date-fns/locale';
import {firstValueFrom} from 'rxjs';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';

@Component({
  selector: 'app-card-note-appointment',
  templateUrl: './card-note-appointment.component.html',
  styleUrls: ['./card-note-appointment.component.scss']
})
export class CardNoteAppointmentComponent implements OnInit {
  es = es;
  @Input() active: boolean;
  @Input() createdBy: IUser | IUsersAppointment;
  @Input() appointmentNote: INoteTimestamp;
  @Input() userLogged: IUser;
  @Input() allowToAssign: boolean;
  @Input() allowToAssignToService: boolean;
  @Input() clinicId: string;
  @Input() serviceId: string;
  @Input() patientId: string;
  /**
   * canInteract - Si es verdadero puede asignar cita y eliminar la nota, en caso contrario no permite estas acciones
   */
  @Input() canInteract = false;
  @Output() changeShared: EventEmitter<any> = new EventEmitter<any>();
  @Output() delete: EventEmitter<{ noteId: string, id: string }> = new EventEmitter<{ noteId: string, id: string }>();
  @Output() getAppointmentsToAssign: EventEmitter<boolean> = new EventEmitter<boolean>();
  confirmModal?: NzModalRef;

  @ViewChild('tplModalSelectAppointment') tplModalSelectAppointment: TemplateRef<any>;
  appointmentsToAssign: MealPlanAppointmentResponseDTO[] = [];
  appointmentIdToAssign: string;
  constructor(
    private modal: NzModalService,
    private store: Store,
    private messageAlertService: MessageAlertService,
  ) { }

  ngOnInit(): void {
  }

  /**
   * Emite al padre cuando se quiere cambiar la propiedad de compartir con el paciente la nota.
   */
  onEmitChangeSharedWithPatient(): void {
    const shareNote = !this.appointmentNote.patientCanView;
    this.changeShared.emit({ noteId: this.appointmentNote._id, isShared: shareNote });
  }

  /**
   * Obtiene las citas a las que se les puede asigar la nota.
   */
  async getAppointmentsToASsign(): Promise<void> {
    try {
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Obteniendo citas ...'))));
      await firstValueFrom(this.store.dispatch(new AppointmentActions.GetToAssign(
        this.clinicId,
        this.serviceId,
        this.patientId
      )));
      this.appointmentsToAssign = cloneDeep(await firstValueFrom((this.store.selectOnce(AppointmentState.getAppointmentsToAssignTo))));
    } catch (e) {
      Logger.log(e?.message);
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  /**
   * Determina si asignar una nota a nivel de servicio o a nivel de cita.
   * @param event any event
   */
  async onAssignToAppointmentOrService(event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    if (this.allowToAssign) {
      await this.assignToAppointment();
    } else if (this.allowToAssignToService) {
      await this.assignToService();
    }
  }

  /**
   * Asigna una nota a una cita.
   */
  async assignToAppointment(): Promise<void> {
    // obtener las citas de redux
    await this.getAppointmentsToASsign();
    if (this.appointmentsToAssign.length === 0) {
      return this.messageAlertService.createMessage('info', 'No hay citas para asignar a la nota');
    }

    this.confirmModal = this.modal.confirm({
      nzTitle: '¿Deseas asignar la nota a otra cita?',
      nzContent: this.tplModalSelectAppointment,
      nzOkText: 'SÍ ASIGNAR',
      nzCancelText: 'NO ASIGNAR',
      nzClassName: 'modal-confirm button-primary',
      nzWidth: '500px',
      nzClosable: false,
      nzOnOk: async () => {
        try {
          await firstValueFrom((this.store.dispatch(new Loader(true, 'Asignado nota a la cita ...'))));
          await firstValueFrom(this.store.dispatch(new ServiceAction.AssignNoteToAppointment(
            this.appointmentNote._id,
            this.appointmentIdToAssign
          )));
          this.appointmentIdToAssign = null;
        } catch (e) {
          Logger.log(e?.message);
        } finally {
          await firstValueFrom((this.store.dispatch(new Loader(false))));
        }
      },
      nzOnCancel: () => {
        this.appointmentIdToAssign = null;
      },
    });
  }

  /**
   * Asigna una nota a un servicio.
   */
  async assignToService(): Promise<void> {
    // obtener las citas de redux
    this.confirmModal = this.modal.confirm({
      nzTitle: '¿Deseas asignar la nota al servicio?',
      nzContent: 'Al finalizar la nota será vista en el panel de notas del servicio.',
      nzOkText: 'SÍ ASIGNAR',
      nzCancelText: 'NO ASIGNAR',
      nzClassName: 'modal-confirm button-primary',
      nzWidth: '500px',
      nzClosable: false,
      nzOnOk: async () => {
        try {
          await firstValueFrom((this.store.dispatch(new Loader(true, 'Asignado nota a la cita ...'))));
          await firstValueFrom(this.store.dispatch(new ServiceAction.AssignNoteToService(
            this.appointmentNote._id,
            this.serviceId,
            this.patientId
          )));
          this.appointmentIdToAssign = null;
        } catch (e) {
          Logger.log(e?.message);
        } finally {
          await firstValueFrom((this.store.dispatch(new Loader(false))));
        }
      },
      nzOnCancel: () => {
        this.appointmentIdToAssign = null;
      },
    });
  }

  /**
   * Elimina una nota.
   * @param event any event
   */
  onDelete(event): void {
    event.preventDefault();
    event.stopPropagation();
    this.confirmModal = this.modal.confirm({
      nzTitle: '¿Estás seguro de eliminar la nota?',
      nzContent: 'Una vez guardado, esta acción no se podrá deshacer.',
      nzOkText: 'SÍ QUIERO ELIMINARLA',
      nzCancelText: 'NO QUIERO',
      nzOkDanger: true,
      nzClassName: 'modal-confirm',
      nzClosable: false,
      nzOnOk: () => {
        this.delete.emit({ noteId: this.appointmentNote.noteId, id: this.appointmentNote._id });
      },
      nzOnCancel: () => {
      }
    });
  }
}
